"use client"

import { Text } from "@/elements"
import { graphql } from "@/gql"
import { ApplyToJobDrawerDocument, CreativeJobsApplicationsDocument } from "@/gql/graphql"
import type { NovaSession } from "@/types"
import { useQuery } from "@apollo/client"
import { FC } from "react"
import { JobsSideDrawerTemplate } from "../JobsSideDrawerTemplate"
import { JobApplicationContent } from "./JobApplicationContent"
import { JobApplicationFooter } from "./JobApplicationFooter"

const JobApplicationDrawerQuery = graphql(`
  query JobApplicationDrawer(
    $jobId: ID!
    $positionId: ID!
    $creativeId: ID!
    $first: Int!
    $after: String
    $category: [ID!]
    $creatorRoles: [ID!]
  ) {
    jobPosting: node(id: $jobId) {
      id
      __typename
      ... on JobPosting {
        ...ApplyToJobContentJobPosting
        ...ApplyToJobFooter
        ...JobApplicationContentJobPosting
        ...AuthenticatedCreativeJobDetailsFooter
      }
    }
    creative: node(id: $creativeId) {
      id
      __typename
      ... on IndividualCreative {
        id
        ...ApplyToJobContentCreative
      }
    }
  }
`)

interface Props {
  serverSession: NovaSession
  onClose: () => void
  open: boolean
  jobPostingId: string | null
  positionId: string | null
  jobPostingHasImage?: boolean
}

export const JobApplicationDrawer: FC<Props> = ({
  serverSession,
  onClose,
  open,
  jobPostingId = "",
  positionId = "",
}) => {
  const creativeId = serverSession?.user?.creative?.id
  const { data, loading } = useQuery(JobApplicationDrawerQuery, {
    variables: {
      jobId: jobPostingId!,
      positionId: positionId!,
      creativeId: creativeId!,
      first: 8,
    },
    skip: !jobPostingId || !positionId || !creativeId,
    fetchPolicy: "cache-and-network",
  })

  const creative = data?.creative?.__typename === "IndividualCreative" ? data?.creative : null

  const jobPosting =
    data?.jobPosting?.__typename === "ClientJobPosting" || data?.jobPosting?.__typename === "CreativeJobPosting"
      ? data?.jobPosting
      : null

  return (
    <>
      <JobsSideDrawerTemplate
        mainContent={
          <>
            <div className="relative mt-6 flex h-12 w-full items-center justify-center px-5 md:px-10">
              <Text>Your application</Text>
            </div>
            <div className="md:px-10">
              <JobApplicationContent
                jobPosting={jobPosting}
                creative={creative}
                loading={loading}
                serverSession={serverSession}
              />
            </div>
          </>
        }
        footer={
          <JobApplicationFooter
            jobPosting={jobPosting}
            returnToJobDetails={onClose}
            withdrawMutationRefetchQueries={[ApplyToJobDrawerDocument, CreativeJobsApplicationsDocument]}
            loading={loading}
          />
        }
        onClose={onClose}
        open={open}
      />
    </>
  )
}
